import { UzButton, UzInput } from '@utazzo/ui';
import { Play, RefreshCw, Square, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useTboHotelSync } from '../use-tbo-hotel-sync';

type Props = {
  canSync: boolean;
};

export const TboHotelSyncPanel = ({ canSync }: Props): React.JSX.Element => {
  const hotelSync = useTboHotelSync();
  const [syncCountry, setSyncCountry] = useState('');
  const [syncCityId, setSyncCityId] = useState('');

  useEffect(() => {
    const cp = hotelSync.savedCheckpoint;
    if (!cp || hotelSync.isRunning) return;
    setSyncCountry(cp.countryCode);
    setSyncCityId(cp.tboCityId ?? '');
  }, [hotelSync.savedCheckpoint, hotelSync.isRunning]);

  const canStart =
    canSync &&
    syncCountry.trim().length >= 2 &&
    !hotelSync.isRunning &&
    hotelSync.uiState.status !== 'running';
  const canResume = canSync && !!hotelSync.savedCheckpoint && !hotelSync.isRunning;

  const progressPct =
    hotelSync.uiState.status === 'running' && hotelSync.uiState.progress.totalInCity > 0
      ? Math.min(
          100,
          Math.round(
            (hotelSync.uiState.progress.processedInCity / hotelSync.uiState.progress.totalInCity) *
              100,
          ),
        )
      : null;

  const startSync = () => {
    void hotelSync.startFresh({
      countryCode: syncCountry.trim(),
      ...(syncCityId ? { tboCityId: syncCityId } : {}),
    });
  };

  return (
    <div className="mt-2 space-y-3">
      {hotelSync.savedCheckpoint && hotelSync.uiState.status !== 'running' && (
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          role="status"
        >
          <div>
            <strong className="font-medium">Paused sync</strong>
            <span className="ml-2">{hotelSync.formatCheckpoint(hotelSync.savedCheckpoint)}</span>
            {hotelSync.uiState.status === 'paused' && hotelSync.uiState.reason === 'error' && (
              <p className="mt-1 text-amber-800">{hotelSync.uiState.message}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <UzButton
              size="sm"
              variant="outline"
              onClick={() => void hotelSync.resume()}
              disabled={!canResume}
            >
              <Play className="mr-1.5 h-3.5 w-3.5" />
              Resume
            </UzButton>
            <UzButton size="sm" variant="ghost" onClick={hotelSync.discardCheckpoint}>
              <X className="mr-1.5 h-3.5 w-3.5" />
              Discard
            </UzButton>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-28">
          <UzInput
            name="tbo-hotel-sync-country"
            label="Country code"
            placeholder="AE"
            value={syncCountry}
            disabled={hotelSync.isRunning}
            onChange={(e) => setSyncCountry(e.target.value.toUpperCase().slice(0, 2))}
          />
        </div>
        <div className="w-40">
          <UzInput
            name="tbo-hotel-sync-city"
            label="TBO city id (optional)"
            placeholder="115936"
            value={syncCityId}
            disabled={hotelSync.isRunning}
            onChange={(e) => setSyncCityId(e.target.value.trim())}
          />
        </div>
        <UzButton onClick={startSync} loading={hotelSync.isRunning} disabled={!canStart}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {syncCityId ? 'Sync city hotels' : 'Sync country hotels'}
        </UzButton>
        {hotelSync.isRunning && (
          <UzButton variant="destructive" onClick={hotelSync.stop}>
            <Square className="mr-2 h-4 w-4" />
            Stop
          </UzButton>
        )}
      </div>

      {hotelSync.uiState.status === 'running' && (
        <div className="mt-3 space-y-1" aria-live="polite">
          <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200">
            <div
              className="h-full rounded-full bg-blue-600 transition-all duration-300"
              style={{
                width:
                  progressPct !== null && progressPct !== undefined ? `${progressPct}%` : '30%',
              }}
            />
          </div>
          <p className="text-xs text-zinc-600">
            {hotelSync.uiState.progress.totalInCity > 0 ? (
              <>
                Batch progress: {hotelSync.uiState.progress.processedInCity}/
                {hotelSync.uiState.progress.totalInCity} hotels in city
                {hotelSync.uiState.progress.currentCityId
                  ? ` ${hotelSync.uiState.progress.currentCityId}`
                  : ''}
                {' · '}
                {hotelSync.uiState.totals.upserted} upserted total
                {hotelSync.uiState.totals.errors > 0
                  ? ` · ${hotelSync.uiState.totals.errors} errors`
                  : ''}
              </>
            ) : (
              <>Syncing… {hotelSync.uiState.totals.upserted} upserted so far</>
            )}
          </p>
          <p className="text-[11px] text-zinc-500">
            Safe to stop - progress is saved after each batch. Use Resume to continue.
          </p>
        </div>
      )}

      <details className="mt-3 text-xs text-zinc-600">
        <summary className="cursor-pointer font-medium text-zinc-700">Manual sync guide</summary>
        <ol className="mt-2 list-decimal space-y-1.5 pl-4">
          <li>
            Run <strong>TBO → Geography → Hotels</strong> after geography sync (countries/cities) is
            complete.
          </li>
          <li>
            Enter a 2-letter <strong>country code</strong> (e.g. <code>AE</code>) and preferably a{' '}
            <strong>TBO city id</strong> from the Cities tab - one city at a time is fastest and
            resumable.
          </li>
          <li>
            Click <strong>Sync city hotels</strong>. Each batch syncs 25 hotels (~10–20 s). The bar
            shows progress for the current city.
          </li>
          <li>
            <strong>Stop</strong> anytime (tab close, network blip, or you need the machine).
            Progress is saved automatically - click <strong>Resume</strong> to pick up where you
            left off.
          </li>
          <li>
            On failure you get an error toast; progress is still saved. Fix the cause (credentials,
            rate limit) and tap <strong>Resume</strong>- upserts are idempotent.
          </li>
          <li>
            <strong>Discard</strong> clears saved progress if you want to start over with a
            different city or country.
          </li>
          <li>
            Large cities (1000+ hotels) take many batches- use <strong>Stop</strong> /
            <strong>Resume</strong>. Hotels beyond an old 500 cap are included; the sync walks the
            full TBO code list.
          </li>
          <li>
            <strong>New hotels</strong> TBO adds later: re-run sync for that city, or rely on
            itinerary <strong>hotel search</strong> (lazy backfill upserts any code TBO returns in
            live search, even if not yet batch-synced).
          </li>
          <li>
            Mirror rows and <strong>last synced</strong> update when a full run completes. Itinerary
            hotel search also lazily fills gaps - manual sync is for pre-warming before launch.
          </li>
        </ol>
      </details>
    </div>
  );
};
